'use client';

import { c as _c } from "react/compiler-runtime";
import { useAccess } from '@repo/auth';
import { permissions } from '@repo/permissions';
import { copyLoadDetailsFieldKeys } from '@repo/schema/page-data';
import { TEST_IDS } from '@repo/sdk/constants';
import type { CommandWorkflowGroup, CommandWorkflowPage } from '@repo/shortcuts/command';
import { Check, Circle, Settings2 } from '@repo/ui/icons';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { COPY_LOAD_DETAILS_FIELD_LABELS, type CopyLoadDetailsSettings, useCopyLoadDetailsSettings } from '../loads/_components/copy-load-details/copy-load-details-settings-provider.tsx';
import { formatLoadDetailsPreview } from '../loads/_components/copy-load-details/formatter.ts';
import { CommandLabel } from './command-label.tsx';
import { usePageCommands } from './command-provider.tsx';
import { isLoadsListPath } from '@/app/@protected/loads/_utils/loads-paths.ts';
const OUTPUT_STYLES = [{
  label: 'Detailed',
  value: 'detailed'
}, {
  label: 'Compact',
  value: 'compact'
}] as const;
const PROFILE_FIELD_GROUPS = [{
  fields: ['routeStopType', 'routeLocationName', 'routeAddress', 'routeDate', 'routeTime'],
  key: 'route',
  label: 'Route'
}, {
  fields: ['cargoGoods', 'cargoPackaging', 'cargoPallets', 'cargoWeight', 'cargoTemperature', 'cargoRequirements', 'vehicleType'],
  key: 'cargo',
  label: 'Customer cargo requirements'
}, {
  fields: ['haulierRate', 'shipperRate'],
  key: 'rate',
  label: 'Rate and payment'
}] as const satisfies readonly {
  fields: readonly (typeof copyLoadDetailsFieldKeys)[number][];
  key: string;
  label: string;
}[];
const FormatPreview = t0 => {
  const $ = _c(7);
  const {
    canCopyHaulierRate,
    canCopyShipperRate,
    settings
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="text-xs font-medium text-foreground">Illustrative format preview</p>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== canCopyHaulierRate || $[2] !== canCopyShipperRate || $[3] !== settings) {
    t2 = formatLoadDetailsPreview(settings, {
      canCopyHaulierRate,
      canCopyShipperRate
    });
    $[1] = canCopyHaulierRate;
    $[2] = canCopyShipperRate;
    $[3] = settings;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <div aria-live="polite" className="px-2 pt-1 pb-2" data-testid={TEST_IDS.cmdk.copyLoadDetails.preview} data-sentry-component="FormatPreview" data-sentry-source-file="copy-load-details-settings-commands.tsx">{t1}<pre className="mt-2 rounded-md bg-muted p-2 text-xs whitespace-pre-wrap text-foreground">{t2}</pre></div>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export const CopyLoadDetailsSettingsCommands = () => {
  const $ = _c(58);
  const {
    canCopyHaulierRate,
    canCopyShipperRate,
    isReady,
    settings,
    update
  } = useCopyLoadDetailsSettings();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      permissions: [permissions.loads.list]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    allowed: canListLoads
  } = useAccess(t0);
  const pathname = usePathname();
  let t1;
  if ($[1] !== pathname) {
    t1 = isLoadsListPath(pathname ?? "") || /^\/loads\/\d+(?:\/|$)/.test(pathname ?? "");
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isLoadsPath = t1;
  let t2;
  if ($[3] !== canListLoads || $[4] !== isLoadsPath || $[5] !== isReady) {
    t2 = canListLoads && isLoadsPath ? [{
      heading: "Loads settings",
      id: "copy-load-details-settings-root-group",
      items: [{
        disabled: !isReady,
        id: "copy-load-details-settings-root",
        kind: "page" as const,
        label: <CommandLabel icon={Settings2}>Copy load details</CommandLabel>,
        pageId: "copy-load-details-settings-page",
        testId: TEST_IDS.cmdk.copyLoadDetails.settings,
        value: "copy load details"
      }]
    }] : [];
    $[3] = canListLoads;
    $[4] = isLoadsPath;
    $[5] = isReady;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const groups = t2;
  let t3;
  bb0: {
    if (!canListLoads || !isLoadsPath) {
      t3 = undefined;
      break bb0;
    }
    let t4;
    if ($[7] !== settings.selectedFields) {
      t4 = new Set(settings.selectedFields);
      $[7] = settings.selectedFields;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    const selectedFields = t4;
    let t5;
    if ($[9] !== canCopyHaulierRate || $[10] !== canCopyShipperRate) {
      t5 = field => field === "haulierRate" ? canCopyHaulierRate : field === "shipperRate" ? canCopyShipperRate : true;
      $[9] = canCopyHaulierRate;
      $[10] = canCopyShipperRate;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    const isFieldAvailable = t5;
    let t6;
    if ($[12] !== selectedFields || $[13] !== update) {
      t6 = fields => update({
        selectedFields: copyLoadDetailsFieldKeys.filter(field_0 => selectedFields.has(field_0) || fields.includes(field_0))
      });
      $[12] = selectedFields;
      $[13] = update;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    const selectFields = t6;
    let t7;
    if ($[15] !== settings.selectedFields || $[16] !== update) {
      t7 = fields_0 => {
        const nextFields = settings.selectedFields.filter(field_1 => !fields_0.includes(field_1));
        if (nextFields.length > 0) {
          update({
            selectedFields: nextFields
          });
        }
      };
      $[15] = settings.selectedFields;
      $[16] = update;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    const clearFields = t7;
    let t8;
    if ($[18] !== clearFields || $[19] !== selectFields || $[20] !== selectedFields) {
      t8 = field_2 => selectedFields.has(field_2) ? clearFields([field_2]) : selectFields([field_2]);
      $[18] = clearFields;
      $[19] = selectFields;
      $[20] = selectedFields;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    const toggleField = t8;
    let t9;
    if ($[22] !== canCopyHaulierRate || $[23] !== canCopyShipperRate || $[24] !== settings) {
      t9 = id => ({
        id,
        kind: "render" as const,
        render: () => <FormatPreview canCopyHaulierRate={canCopyHaulierRate} canCopyShipperRate={canCopyShipperRate} settings={settings} />
      });
      $[22] = canCopyHaulierRate;
      $[23] = canCopyShipperRate;
      $[24] = settings;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    const preview = t9;
    let t10;
    if ($[26] !== isReady || $[27] !== settings.outputStyle || $[28] !== update) {
      t10 = OUTPUT_STYLES.map(style => ({
        closeOnSelect: false,
        disabled: !isReady || style.value === settings.outputStyle,
        id: `copy-load-details-output-style-${style.value}`,
        kind: "action" as const,
        label: <CommandLabel icon={style.value === settings.outputStyle ? Check : Circle}>{style.label}</CommandLabel>,
        onSelect: context => {
          update({
            outputStyle: style.value
          });
          context.goBack();
        },
        testId: TEST_IDS.cmdk.copyLoadDetails.outputStyle(style.value),
        value: style.label
      }));
      $[26] = isReady;
      $[27] = settings.outputStyle;
      $[28] = update;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    let t11;
    if ($[30] !== t10) {
      t11 = {
        id: "copy-load-details-settings-output-styles",
        items: t10
      };
      $[30] = t10;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    let t12;
    if ($[32] !== preview) {
      t12 = {
        id: "copy-load-details-settings-output-style-preview",
        items: [preview("copy-load-details-output-style-preview")]
      };
      $[32] = preview;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    let t13;
    if ($[34] !== t11 || $[35] !== t12) {
      t13 = {
        groups: [t11, t12],
        inputPlaceholder: "Search output styles...",
        title: "Output style"
      };
      $[34] = t11;
      $[35] = t12;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    let t14;
    if ($[37] !== isReady || $[38] !== preview) {
      let t15;
      if ($[40] !== isReady) {
        t15 = group => ({
          id: `copy-load-details-settings-${group.key}`,
          items: [{
            disabled: !isReady,
            id: `copy-load-details-settings-${group.key}`,
            kind: "page" as const,
            label: <CommandLabel icon={Settings2}>{group.label}</CommandLabel>,
            pageId: `copy-load-details-settings-${group.key}-fields`,
            testId: TEST_IDS.cmdk.copyLoadDetails.fieldGroup(group.key),
            value: `${group.label} fields`
          }]
        });
        $[40] = isReady;
        $[41] = t15;
      } else {
        t15 = $[41];
      }
      const t16 = !isReady;
      let t17;
      if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = <CommandLabel icon={Settings2}>Output style</CommandLabel>;
        $[42] = t17;
      } else {
        t17 = $[42];
      }
      let t18;
      if ($[43] !== t16) {
        t18 = {
          id: "copy-load-details-settings-output",
          items: [{
            disabled: t16,
            id: "copy-load-details-settings-output-style",
            kind: "page" as const,
            label: t17,
            pageId: "copy-load-details-settings-output-style-page",
            testId: TEST_IDS.cmdk.copyLoadDetails.outputStylePage,
            value: "output style"
          }]
        };
        $[43] = t16;
        $[44] = t18;
      } else {
        t18 = $[44];
      }
      let t19;
      if ($[45] !== preview) {
        t19 = {
          id: "copy-load-details-settings-preview",
          items: [preview("copy-load-details-settings-format-preview")]
        };
        $[45] = preview;
        $[46] = t19;
      } else {
        t19 = $[46];
      }
      t14 = {
        groups: [...PROFILE_FIELD_GROUPS.map(t15), t18, t19]
      };
      $[37] = isReady;
      $[38] = preview;
      $[39] = t14;
    } else {
      t14 = $[39];
    }
    let pages;
    if ($[47] !== clearFields || $[48] !== isFieldAvailable || $[49] !== isReady || $[50] !== preview || $[51] !== selectFields || $[52] !== selectedFields || $[53] !== settings.selectedFields || $[54] !== t13 || $[55] !== t14 || $[56] !== toggleField) {
      pages = {
        "copy-load-details-settings-output-style-page": t13,
        "copy-load-details-settings-page": t14
      };
      for (const group_0 of PROFILE_FIELD_GROUPS) {
        const availableFields = group_0.fields.filter(isFieldAvailable);
        const selectedCount = availableFields.filter(field_3 => selectedFields.has(field_3)).length;
        const canClearAll = settings.selectedFields.length > selectedCount;
        pages[`copy-load-details-settings-${group_0.key}-fields`] = {
          groups: [{
            id: `copy-load-details-${group_0.key}-bulk-actions`,
            items: [{
              closeOnSelect: false,
              disabled: !isReady || selectedCount === availableFields.length,
              id: `copy-load-details-${group_0.key}-select-all`,
              kind: "action" as const,
              label: <CommandLabel icon={Check}>Select all</CommandLabel>,
              onSelect: () => selectFields(availableFields),
              testId: TEST_IDS.cmdk.copyLoadDetails.selectAll(group_0.key),
              value: `select all ${group_0.label}`
            }, {
              closeOnSelect: false,
              disabled: !isReady || selectedCount === 0 || !canClearAll,
              id: `copy-load-details-${group_0.key}-clear-all`,
              kind: "action" as const,
              label: <CommandLabel icon={Circle}>Clear all</CommandLabel>,
              onSelect: () => clearFields(group_0.fields),
              testId: TEST_IDS.cmdk.copyLoadDetails.clearAll(group_0.key),
              value: `clear all ${group_0.label}`
            }]
          }, {
            id: `copy-load-details-${group_0.key}-fields`,
            items: availableFields.map(field_4 => {
              const selected = selectedFields.has(field_4);
              const isLastSelectedField = selected && settings.selectedFields.length === 1;
              return {
                closeOnSelect: false,
                disabled: !isReady || isLastSelectedField,
                id: `copy-load-details-field-${field_4}`,
                kind: "action" as const,
                label: <CommandLabel icon={selected ? Check : Circle}>{COPY_LOAD_DETAILS_FIELD_LABELS[field_4]}{isLastSelectedField ? " (one field must remain)" : ""}</CommandLabel>,
                onSelect: () => toggleField(field_4),
                testId: TEST_IDS.cmdk.copyLoadDetails.field(field_4),
                value: COPY_LOAD_DETAILS_FIELD_LABELS[field_4]
              };
            })
          }, {
            id: `copy-load-details-${group_0.key}-preview`,
            items: [preview(`copy-load-details-${group_0.key}-preview`)]
          }],
          inputPlaceholder: `Search ${group_0.label.toLowerCase()} fields...`,
          title: group_0.label
        };
      }
      $[47] = clearFields;
      $[48] = isFieldAvailable;
      $[49] = isReady;
      $[50] = preview;
      $[51] = selectFields;
      $[52] = selectedFields;
      $[53] = settings.selectedFields;
      $[54] = t13;
      $[55] = t14;
      $[56] = toggleField;
      $[57] = pages;
    } else {
      pages = $[57];
    }
    t3 = pages;
  }
  const nestedPages = t3;
  usePageCommands("copy-load-details-settings", groups, nestedPages);
  return null;
};